import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, ExternalLink, ArrowLeft, ArrowRight, Share2, Check } from "lucide-react";
import { toast } from "sonner";
import AuthAwareShell from "@/components/AuthAwareShell";

export function Downloads() {
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/resources").then(({ data }) => {
      setResources(data.items || []);
      setCategories(data.categories || []);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = cat === "all" ? resources : resources.filter((r) => r.category === cat);

  return (
    <AuthAwareShell>
      <div data-testid="downloads-page" className="px-5 sm:px-6 lg:px-10 py-8 sm:py-10 max-w-6xl">
        <div className="dms-label mb-3">Resource library</div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-[#0A2342]">Resources</h1>
        <p className="mt-2 text-[#475569] text-[14.5px]">Practical guides, templates, and tools we actually use to grow dive businesses.</p>

        <div className="mt-6 flex flex-wrap gap-2">
          <button onClick={() => setCat("all")} className={`px-3 py-1.5 rounded-full text-[12.5px] font-semibold ${cat === "all" ? "bg-[#0A2342] text-white" : "bg-white border border-[#E2E8F0] text-[#1F2937]"}`}>All</button>
          {categories.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className={`px-3 py-1.5 rounded-full text-[12.5px] font-semibold ${cat === c ? "bg-[#0A2342] text-white" : "bg-white border border-[#E2E8F0] text-[#1F2937]"}`}>{c}</button>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-full text-[#64748B] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : filtered.map((r) => (
            <Link key={r.slug} to={`/downloads/${r.slug}`} data-testid={`resource-${r.slug}`} className="dms-card dms-card-hover p-5 sm:p-6">
              <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#4FD1C5]">{r.category}</div>
              <h3 className="mt-2 font-heading font-bold text-[16px] text-[#0A2342] leading-snug">{r.title}</h3>
              <p className="mt-2 text-[13.5px] text-[#475569] leading-relaxed line-clamp-3">{r.excerpt}</p>
              <div className="mt-4 text-[12.5px] font-semibold text-[#1D4E89] inline-flex items-center gap-1.5">Read article <ArrowRight className="w-3.5 h-3.5" /></div>
            </Link>
          ))}
        </div>
      </div>
    </AuthAwareShell>
  );
}

export function Resource() {
  const { slug } = useParams();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    api.get(`/resources/${slug}`).then(({ data }) => setR(data.resource)).finally(() => setLoading(false));
  }, [slug]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const shareUrl = `${backendUrl}/api/share/downloads/${slug}`;

  const handleShare = async () => {
    const shareData = {
      title: r?.title || "Dive Media Strategist",
      text: r?.excerpt || "",
      url: shareUrl,
    };
    // Prefer the native share sheet on mobile / supported desktop
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (e) {
        if (e.name === "AbortError") return; // user closed the sheet, no toast
      }
    }
    // Fallback: copy the OG-optimised URL to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied to clipboard");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Could not copy the link. Try selecting and copying manually.");
    }
  };

  if (loading) return <AuthAwareShell><div className="p-10 text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div></AuthAwareShell>;
  if (!r) return <AuthAwareShell><div className="p-10">Not found.</div></AuthAwareShell>;

  return (
    <AuthAwareShell>
      <article data-testid="resource-page" className="px-5 sm:px-6 lg:px-10 py-8 sm:py-12 max-w-3xl">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <Link to="/downloads" data-testid="resource-back" className="text-[13.5px] text-[#1D4E89] hover:text-[#0A2342] inline-flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> All resources</Link>
          <button
            onClick={handleShare}
            data-testid="resource-share-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#E2E8F0] bg-white px-3 py-1.5 text-[12.5px] font-semibold text-[#0A2342] hover:border-[#4FD1C5] hover:text-[#0F766E] transition-colors"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-[#0F766E]" /> : <Share2 className="w-3.5 h-3.5" />}
            {copied ? "Copied" : "Share this guide"}
          </button>
        </div>
        <div className="dms-label mb-3">{r.category}</div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342] leading-tight">{r.title}</h1>
        <p className="mt-3 text-[#475569] text-[15.5px] leading-relaxed">{r.excerpt}</p>
        <div className="mt-8 prose prose-slate max-w-none text-[#1F2937] text-[15.5px] leading-relaxed">
          {r.body.split("\n\n").map((para, i) => (
            para.startsWith("## ") ? (
              <h2 key={i} className="font-heading font-bold text-xl text-[#0A2342] mt-8 mb-3">{para.replace(/^## /, "")}</h2>
            ) : (
              <p key={i} className="my-4 whitespace-pre-line">{para}</p>
            )
          ))}
        </div>
        {r.affiliate_url && (
          <div className="mt-10 dms-card p-6 bg-[#F8FAFC]">
            <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#64748B]">Recommended tool · affiliate link</div>
            <a href={r.affiliate_url} target="_blank" rel="nofollow noopener noreferrer" data-testid="resource-affiliate" className="mt-3 inline-flex items-center gap-2 dms-btn-accent">
              {r.affiliate_label || "Try this tool"} <ExternalLink className="w-4 h-4" />
            </a>
            <p className="mt-3 text-[11.5px] text-[#94A3B8]">We may earn a commission if you sign up via this link. We only recommend tools we use ourselves.</p>
          </div>
        )}
      </article>
    </AuthAwareShell>
  );
}

export default Downloads;
